import { useCallback, useEffect, useState } from "react";
import CegjelzoSearch from "../../components/cegjelzo/CegjelzoSearch.jsx";
import CegjelzoSearchHeader from "../../components/cegjelzo/CegjelzoSearchHeader.jsx";
import CegjelzoSelectedCompanySummary from "../../components/cegjelzo/CegjelzoSelectedCompanySummary.jsx";
import {
  CEGJELZO_REGISTRY_TYPES,
  isCegjelzoRegistryTypeAutoFillSupported,
  mapCegjelzoCompanyToNoOfEmployees,
  normalizeCegjelzoRegistryType,
} from "../../utils/cegjelzo/mapCegjelzoCompanyData.js";
import {
  applyCegjelzoCompanyTypeToStore,
  applyCegjelzoVatFilterToStore,
  applyCegjelzoYearlyRevenueToStore,
} from "../../utils/cegjelzo/persistCegjelzoCompany.js";
import i18n from "../../../../utils/i18n";
import classes from "./commonCegjelzoAuto.module.scss";

const CommonCegjelzoAuto = ({
  selectedCompany,
  cegjelzoPersistError,
  onClearCegjelzoPersistError,
  onSetCegjelzoPersistError,
  addCompanyInfo,
  setIndustrySelection,
  clearCegjelzoData,
  saveCegjelzoData,
  setCompanyTypeFlowSelection,
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // The collapsing header shifts the search field upward; nudge any open
  // autocomplete popper (anchored to the field) to recompute its position
  // once the layout has settled, otherwise it stays anchored to the stale,
  // pre-collapse coordinates.
  const handleHeaderTransitionEnd = useCallback(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);

  const isSupportedCompanyType =
    selectedCompany == null ||
    isCegjelzoRegistryTypeAutoFillSupported(selectedCompany.type);

  const syncNoOfEmployeesToRedux = useCallback(
    (companyData) => {
      if (typeof addCompanyInfo !== "function") {
        return;
      }

      const registryType = normalizeCegjelzoRegistryType(companyData?.type);
      const noofemployees = mapCegjelzoCompanyToNoOfEmployees(companyData);

      if (registryType === CEGJELZO_REGISTRY_TYPES.COMPANIES) {
        addCompanyInfo({
          id: "noofemployees",
          data: noofemployees ?? 0,
          stateObject: "company_store",
        });
        addCompanyInfo({
          id: "noofemployeesSource",
          data: "default",
          stateObject: "company_store",
        });
        return;
      }

      addCompanyInfo({
        id: "noofemployees",
        data: 0,
        stateObject: "company_store",
      });
      addCompanyInfo({
        id: "noofemployeesSource",
        data: "default",
        stateObject: "company_store",
      });
    },
    [addCompanyInfo],
  );

  const syncCompanyTypeFromCegjelzo = useCallback(
    (companyData) => {
      applyCegjelzoCompanyTypeToStore({
        companyData,
        setCompanyTypeFlowSelection,
        addCompanyInfo,
      });
    },
    [addCompanyInfo, setCompanyTypeFlowSelection],
  );

  const syncYearlyRevenueFromCegjelzo = useCallback(
    (companyData) => {
      applyCegjelzoYearlyRevenueToStore({
        companyData,
        addCompanyInfo,
        fallBackToIndustryDefaults: true,
      });
    },
    [addCompanyInfo],
  );

  const syncVatFilterFromCegjelzo = useCallback(
    (companyData) => {
      applyCegjelzoVatFilterToStore({
        companyData,
        addCompanyInfo,
      });
    },
    [addCompanyInfo],
  );

  const syncSelectedCompanyToStore = useCallback(
    (companyData) => {
      if (!companyData) {
        return;
      }
      syncNoOfEmployeesToRedux(companyData);
      syncCompanyTypeFromCegjelzo(companyData);
      syncYearlyRevenueFromCegjelzo(companyData);
      syncVatFilterFromCegjelzo(companyData);
    },
    [
      syncCompanyTypeFromCegjelzo,
      syncNoOfEmployeesToRedux,
      syncVatFilterFromCegjelzo,
      syncYearlyRevenueFromCegjelzo,
    ],
  );

  useEffect(() => {
    syncSelectedCompanyToStore(selectedCompany);
  }, [selectedCompany, syncSelectedCompanyToStore]);

  const handleCompanySelected = useCallback(
    (companyData) => {
      onClearCegjelzoPersistError?.();
      saveCegjelzoData(companyData);
    },
    [onClearCegjelzoPersistError, saveCegjelzoData],
  );

  const handleRemoveSelectedCompany = useCallback(async () => {
    onClearCegjelzoPersistError?.();
    try {
      if (typeof setIndustrySelection === "function") {
        await setIndustrySelection("");
      }
      clearCegjelzoData();
      if (typeof addCompanyInfo === "function") {
        addCompanyInfo({
          id: "noofemployees",
          data: 0,
          stateObject: "company_store",
        });
        addCompanyInfo({
          id: "yearlyrevenue",
          data: 0,
          stateObject: "company_store",
        });
        addCompanyInfo({
          id: "vatFilter",
          data: "",
          stateObject: "company_store",
        });
      }
    } catch (error) {
      console.error("Cegjelzo industry clear error:", error);
      onSetCegjelzoPersistError?.(
        i18n.t("searchflow.cegjelzoclearerror"),
      );
    }
  }, [
    addCompanyInfo,
    clearCegjelzoData,
    onClearCegjelzoPersistError,
    onSetCegjelzoPersistError,
    setIndustrySelection,
  ]);

  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header
              className={`${classes.header} ${
                isSearchFocused ? classes.headerHiddenOnFocus : ""
              }`}
              onTransitionEnd={handleHeaderTransitionEnd}
            >
              <CegjelzoSearchHeader
                titleClassName={classes.title}
                subtitleClassName={classes.subtitle}
              />
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.body}>
                {!selectedCompany && (
                  <CegjelzoSearch
                    onCompanySelected={handleCompanySelected}
                    onSearchFocusChange={setIsSearchFocused}
                  />
                )}

                {selectedCompany && (
                  <CegjelzoSelectedCompanySummary
                    companyData={selectedCompany}
                    isSupportedCompanyType={isSupportedCompanyType}
                    unsupportedMessage={i18n.t("searchflow.cegjelzounsupported")}
                    onRemove={handleRemoveSelectedCompany}
                  />
                )}

                {cegjelzoPersistError && (
                  <p className={classes.error}>{cegjelzoPersistError}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonCegjelzoAuto;
